import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import ControlledCheckbox from "@dashboard/components/ControlledCheckbox";
import Form from "@dashboard/components/Form";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DashboardModal } from "@dashboard/components/Modal";
import { OrderErrorFragment } from "@dashboard/graphql";
import useModalDialogErrors from "@dashboard/hooks/useModalDialogErrors";
import { buttonMessages } from "@dashboard/intl";
import { getFormErrors } from "@dashboard/utils/errors";
import getOrderErrorMessage from "@dashboard/utils/errors/order";
import { TextField } from "@material-ui/core";
import { Text } from "@saleor/macaw-ui-next";
import { FormattedMessage, useIntl } from "react-intl";

import { PONTO_RECOLHA_ADDRESS_MAX_LENGTH, pontoRecolhaMessages } from "../../utils/pontoRecolha";

interface FormData {
  trackingNumber: string;
  notifyCustomer: boolean;
  pontoRecolhaAddress?: string;
}

interface OrderFulfillmentTrackingDialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  errors: OrderErrorFragment[];
  open: boolean;
  trackingNumber: string;
  // GLS pickup-point ("Ponto de Recolha") orders: staff must also enter the shop
  // address, sent to the customer in the same shipping email.
  isParcelShop?: boolean;
  pontoRecolhaAddress?: string;
  onClose: () => any;
  onConfirm: (data: FormData) => any;
}

const OrderFulfillmentTrackingDialog = ({
  confirmButtonState,
  errors: apiErrors,
  open,
  trackingNumber,
  isParcelShop = false,
  pontoRecolhaAddress,
  onConfirm,
  onClose,
}: OrderFulfillmentTrackingDialogProps) => {
  const intl = useIntl();
  const errors = useModalDialogErrors(apiErrors, open);
  const formFields = ["trackingNumber", "pontoRecolhaAddress"];
  const formErrors = getFormErrors(formFields, errors);
  const initialData: FormData = {
    trackingNumber: trackingNumber || "",
    notifyCustomer: true,
    ...(isParcelShop && { pontoRecolhaAddress: pontoRecolhaAddress || "" }),
  };

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="xs">
        <Form initial={initialData} onSubmit={onConfirm}>
          {({ change, data, submit }) => (
            <DashboardModal.Grid>
              <DashboardModal.Header>
                <FormattedMessage
                  id="/BJQIq"
                  defaultMessage="Add Tracking Code"
                  description="dialog header"
                />
              </DashboardModal.Header>

              <TextField
                error={!!formErrors.trackingNumber}
                helperText={getOrderErrorMessage(formErrors.trackingNumber, intl)}
                label={intl.formatMessage({
                  id: "yT/GAp",
                  defaultMessage: "Tracking number",
                })}
                name="trackingNumber"
                onChange={change}
                value={data.trackingNumber}
                fullWidth
                data-test-id="tracking-number-input"
              />

              {isParcelShop && (
                <TextField
                  error={!!formErrors.pontoRecolhaAddress}
                  helperText={
                    getOrderErrorMessage(formErrors.pontoRecolhaAddress, intl) ||
                    intl.formatMessage(pontoRecolhaMessages.addressHelperText)
                  }
                  label={intl.formatMessage(pontoRecolhaMessages.addressLabel)}
                  name="pontoRecolhaAddress"
                  onChange={change}
                  value={data.pontoRecolhaAddress}
                  required
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: PONTO_RECOLHA_ADDRESS_MAX_LENGTH }}
                  fullWidth
                  data-test-id="ponto-recolha-address-input"
                />
              )}

              <ControlledCheckbox
                data-test-id="notify-customer"
                name="notifyCustomer"
                label={intl.formatMessage({
                  id: "W2QrR4",
                  defaultMessage: "Send shipping confirmation to the customer",
                  description: "add tracking number, notify customer checkbox",
                })}
                checked={data.notifyCustomer}
                onChange={change}
              />

              {errors.length > 0 && (
                <>
                  <FormSpacer />
                  {errors
                    .filter(err => err.field && !formFields.includes(err.field))
                    .map((err, index) => (
                      <Text display="block" color="critical1" key={index}>
                        {getOrderErrorMessage(err, intl)}
                      </Text>
                    ))}
                </>
              )}

              <DashboardModal.Actions>
                <BackButton onClick={onClose} />
                <ConfirmButton
                  data-test-id="confirm-tracking-number-button"
                  transitionState={confirmButtonState}
                  onClick={submit}
                  disabled={isParcelShop && !data.pontoRecolhaAddress?.trim()}
                >
                  <FormattedMessage {...buttonMessages.confirm} />
                </ConfirmButton>
              </DashboardModal.Actions>
            </DashboardModal.Grid>
          )}
        </Form>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

OrderFulfillmentTrackingDialog.displayName = "OrderFulfillmentTrackingDialog";
export default OrderFulfillmentTrackingDialog;
